import { Controller } from '@hotwired/stimulus';
import { AgCharts } from "ag-charts-enterprise";
import { LicenseManager } from "ag-charts-enterprise";
import { ChartRegistry } from './chart_registry';

LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-090159}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Teamworks}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Teamworks_Operations}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Teamworks_Operations}_need_to_be_licensed___{Teamworks_Operations}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{17_June_2026}____[v3]_[0102]_MTc4MTY1MDgwMDAwMA==363c03ac1222ca6af17656aa63ab479a");

export default class ChartBaseController extends Controller {
  static targets = ['chart'];

  static values = {
    source: String,
    title: String,
    footnote: String,
  }

  observerOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.3,
  }

  connect() {
    this.chartRendered = false;
    this.chartInstance = null;

    // Add loading class to chart target
    this.chartTarget.classList.add('chart-loading');

    // Skip JSON fetching unless a source URL is provided
    if (this.hasSourceValue) {
      this.observer = new IntersectionObserver(
        this.observerCallback.bind(this),
        this.observerOptions
      );

      this.observer.observe(this.chartTarget);
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // public method called from external controllers
  async initializeChart(force = false) {
    // If we've never rendered the chart, render it now
    if (!this.chartRendered) {
      this.chartRendered = true;
      await this.#fetchDataAndRenderChart();
    }
    else if (force) {
      // If already rendered, *don't* re-created the chart; just re-register the same chart instance if needed
      if (this.chartInstance) {
        ChartRegistry.register(this.chartInstance);
      }
    }
  }

  async #fetchDataAndRenderChart() {
    try {
      const data = await this.#fetchChartData();
      const formattedData = this.formatData(data);
      this.renderChart(formattedData);
    } catch (error) {
      console.error('Error fetching data or rendering chart:', error);
    }
  }

  async #fetchChartData() {
    const response = await fetch(this.sourceValue, {
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok.');
    }

    return response.json();
  }

  // These methods should be implemented by child classes
  formatData() {
    throw new Error('formatData must be implemented by child class');
  }

  // Options shared across all chart types
  getChartOptions(data) {
    return {
      container: this.chartTarget,
      data: data,
      footnote: {
        text: this.footnoteValue,
      },
      legend: {
        position: 'right',
      },
      theme: 'ag-material',
    }
  }

  observerCallback(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.chartRendered) {
        this.chartRendered = true;
        this.#fetchDataAndRenderChart();
      }
    })
  }

  renderChart(data) {
    // Remove loading class when we render the chart
    this.chartTarget.classList.remove('chart-loading');

    // Clear the loading spinner by emptying the chart target
    this.chartTarget.innerHTML = '';

    const options = this.getChartOptions(data);
    this.chartInstance = AgCharts.create(options);

    ChartRegistry.register(this.chartInstance);

    this.element.dispatchEvent(new CustomEvent("chart:initialized", {
      bubbles: true,
      detail: { chart: this.chartInstance }
    }));
  }

  async getChartImageUrl() {
    const dataUrl = await this.localChart.getImageDataURL({ type: 'image/png' });
    return dataUrl;
  }

  printUrl(dataUrl) {
    console.log(dataUrl);
  }
}
